import { db } from "@/lib/firebase";
import type { Business } from "@/lib/types";
import SeoHead from "@/components/seo/SeoHead";
import {
  collection,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import React, { ReactNode, useEffect, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";

type LoaderState = {
  status: "loading" | "ready" | "notfound" | "error";
  business: Business | null;
  error: string | null;
  noIndex: boolean;
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
  "",
  "active",
  "trialing",
  "paid",
  "approved",
  "enabled",
]);

function cleanString(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

function isActiveSubscriptionStatus(value: unknown): boolean {
  const status = cleanString(value).toLowerCase();

  /**
   * Empty / missing subscriptionStatus is allowed for now so existing
   * approved Pioneer businesses do not disappear before you backfill
   * subscription fields.
   */
  return ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

function isPublicVisibleBusiness(business: Business): boolean {
  const b = business as any;

  return (
    b.status === "approved" &&
    b.isPublished === true &&
    b.isDeleted !== true &&
    b.isDeactivated !== true &&
    isActiveSubscriptionStatus(b.subscriptionStatus)
  );
}

function NonIndexableBusinessHead() {
  return (
    <SeoHead
      title="Business not found | Soko Grid"
      description="This Soko Grid business profile is not publicly available."
      noIndex
    />
  );
}

export default function PublicBusinessLoader({
  slug,
  children,
}: {
  slug: string;
  children: (business: Business) => ReactNode;
}) {
  const [state, setState] = useState<LoaderState>({
    status: "loading",
    business: null,
    error: null,
    noIndex: false,
  });

  useEffect(() => {
    const s = String(slug || "").trim();

    if (!s) {
      setState({
        status: "error",
        business: null,
        error: "Missing business slug.",
        noIndex: true,
      });
      return;
    }

    setState({
      status: "loading",
      business: null,
      error: null,
      noIndex: false,
    });

    /**
     * Public visibility:
     * - approved
     * - published
     * - not deleted
     * - not deactivated
     *
     * Important:
     * This requires approved business docs to have:
     * isDeactivated: false
     *
     * New drafts already get this field from onboardingBusiness.ts.
     * Existing approved businesses may need a one-time backfill.
     */
    const qy = query(
      collection(db, "businesses"),
      where("slug", "==", s),
      where("status", "==", "approved"),
      where("isPublished", "==", true),
      where("isDeleted", "==", false),
      where("isDeactivated", "==", false),
      limit(1)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        if (snap.empty) {
          setState({
            status: "notfound",
            business: null,
            error: null,
            noIndex: true,
          });
          return;
        }

        const business = {
          id: snap.docs[0].id,
          ...(snap.docs[0].data() as any),
        } as Business;

        if (!isPublicVisibleBusiness(business)) {
          setState({
            status: "notfound",
            business: null,
            error: null,
            noIndex: true,
          });
          return;
        }

        setState({
          status: "ready",
          business,
          error: null,
          noIndex: false,
        });
      },
      (err: any) => {
        console.log(
          "PublicBusinessLoader snapshot error:",
          err?.code,
          err?.message
        );

        setState({
          status: "error",
          business: null,
          error: err?.message || "Could not load this business right now.",
          noIndex: false,
        });
      }
    );

    return () => unsub();
  }, [slug]);

  if (state.status === "loading") {
    return (
      <View style={centerWrap}>
        <ActivityIndicator />

        <Text style={loadingText}>Loading business...</Text>
      </View>
    );
  }

  if (state.status === "error") {
    return (
      <View style={messageWrap}>
        {state.noIndex ? <NonIndexableBusinessHead /> : null}
        <Text style={messageTitle}>
          {state.error || "Could not load this business right now."}
        </Text>
      </View>
    );
  }

  if (state.status === "notfound") {
    return (
      <View style={messageWrap}>
        <NonIndexableBusinessHead />
        <Text style={messageTitle}>Business not found.</Text>

        <Text style={messageText}>
          It may be pending review, unpublished, deactivated, expired, deleted,
          or the slug may be wrong.
        </Text>
      </View>
    );
  }

  return <>{children(state.business!)}</>;
}

const centerWrap = {
  flex: 1,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  padding: 24,
};

const loadingText = {
  marginTop: 10,
  fontWeight: "800" as const,
  color: "#6b7280",
};

const messageWrap = {
  flex: 1,
  padding: 24,
  justifyContent: "center" as const,
};

const messageTitle = {
  fontWeight: "900" as const,
  color: "#111827",
};

const messageText = {
  marginTop: 6,
  color: "#6b7280",
  fontWeight: "700" as const,
  lineHeight: 20,
};
